import type { TokenStorage } from '@alter/api-client';
import { useAuthStore, type Perfil } from './authStore';

/** Clave del perfil (nombres para mostrar) en `Storage`. */
export const PERFIL_STORAGE_KEY = 'alter_perfil';

/**
 * Bridge entre `useAuthStore` (estado en memoria) y la persistencia entre
 * sesiones. Diseño pura-suscripción: el store no conoce el storage; este
 * módulo se encarga.
 *
 * Llamar UNA vez al boot, después de `bootstrapApi`. Hace dos cosas:
 *
 * 1. Hidrata: lee el token guardado (y el perfil, si se pasa
 *    `perfilStorage`) y lo inyecta en el store
 * 2. Suscribe: cualquier cambio futuro de `token` o del perfil se persiste
 *
 * El perfil (`usuarioNombre`, `tenantNombre`) se persiste aparte porque el
 * JWT no lo trae: sin esto, el nombre del usuario desaparece al recargar.
 *
 * La función es `async` para que el caller (`main.tsx`) pueda esperar la
 * hidratación inicial antes de montar React — así `RequireAuth` ve el
 * token del primer render y no flashea `/login` cuando hay sesión válida.
 */
export async function syncAuthStoreWithTokenStorage(
  tokenStorage: TokenStorage,
  perfilStorage?: Storage,
): Promise<void> {
  // 1) Hidratación inicial.
  const initialToken = await tokenStorage.get();
  if (initialToken !== null) {
    useAuthStore.getState().setToken(initialToken);
    const perfil = perfilStorage ? leerPerfil(perfilStorage) : null;
    if (perfil) useAuthStore.getState().setPerfil(perfil);
  }

  // 2) Suscripción para cambios futuros.
  let lastToken = useAuthStore.getState().token;
  let lastPerfil = perfilSerializado(useAuthStore.getState());
  useAuthStore.subscribe((state) => {
    if (state.token !== lastToken) {
      lastToken = state.token;
      if (state.token === null) {
        void tokenStorage.clear();
      } else {
        void tokenStorage.set(state.token);
      }
    }
    if (!perfilStorage) return;
    const perfil = perfilSerializado(state);
    if (perfil === lastPerfil) return;
    lastPerfil = perfil;
    if (state.token === null) {
      perfilStorage.removeItem(PERFIL_STORAGE_KEY);
    } else {
      perfilStorage.setItem(PERFIL_STORAGE_KEY, perfil);
    }
  });
}

function perfilSerializado(state: Perfil): string {
  return JSON.stringify({ usuarioNombre: state.usuarioNombre, tenantNombre: state.tenantNombre });
}

function leerPerfil(storage: Storage): Perfil | null {
  try {
    const raw = storage.getItem(PERFIL_STORAGE_KEY);
    if (raw === null) return null;
    const data = JSON.parse(raw) as Partial<Record<keyof Perfil, unknown>>;
    return {
      usuarioNombre: typeof data.usuarioNombre === 'string' ? data.usuarioNombre : null,
      tenantNombre: typeof data.tenantNombre === 'string' ? data.tenantNombre : null,
    };
  } catch {
    return null;
  }
}
